import {
  Directive,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type B3DsDatepickerBackground = 'white' | 'grey';

type B3DsDatepickerElement = HTMLElement & {
  value?: string;
  label?: string;
  placeholder?: string;
  hint?: string;
  error?: boolean;
  disabled?: boolean;
  min?: string;
  max?: string;
  background?: B3DsDatepickerBackground;
  locale?: string;
  requestUpdate?: () => void;
};

@Directive({
  selector: 'b3-datepicker[formControlName], b3-datepicker[ngModel]',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => B3DsDatepickerDirective),
      multi: true,
    },
  ],
})
export class B3DsDatepickerDirective implements ControlValueAccessor, OnInit, OnChanges {
  @Input() label = '';
  @Input() placeholder = 'DD/MM/AAAA';
  @Input() hint = '';
  @Input() error = false;
  @Input() background: B3DsDatepickerBackground = 'grey';
  @Input() inputId = '';
  @Input() min = '';
  @Input() max = '';
  @Input() disabled = false;
  @Input() locale = 'pt-BR';
  @Input() popoverLeftOffset = '';

  private static readonly POPOVER_OFFSET_STYLE_ID = 'b3-ds-datepicker-popover-offset';

  private onChange: (value: Date | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private value: Date | null = null;
  private formDisabled = false;

  constructor(
    private readonly elementRef: ElementRef<B3DsDatepickerElement>,
    private readonly renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    this.syncElementState();
    this.applyPopoverLeftOffset();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['label'] ||
      changes['placeholder'] ||
      changes['hint'] ||
      changes['error'] ||
      changes['background'] ||
      changes['inputId'] ||
      changes['min'] ||
      changes['max'] ||
      changes['disabled'] ||
      changes['locale']
    ) {
      this.syncElementState();
    }

    if (changes['popoverLeftOffset']) {
      this.applyPopoverLeftOffset();
    }
  }

  writeValue(value: Date | string | null): void {
    if (value instanceof Date && !isNaN(value.getTime())) {
      this.value = value;
    } else if (typeof value === 'string' && value) {
      this.value = this.isoToDate(value);
    } else {
      this.value = null;
    }

    const element = this.elementRef.nativeElement;
    const nextValue = this.value ? this.dateToIso(this.value) : '';
    element.value = nextValue;
    element.requestUpdate?.();
    this.syncElementState();
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled = isDisabled;
    this.syncElementState();
  }

  @HostListener('b3-change', ['$event'])
  onB3Change(event: Event): void {
    const customEvent = event as CustomEvent<{ value?: string }>;
    const iso = customEvent.detail?.value ?? '';
    this.value = iso ? this.isoToDate(iso) : null;
    this.syncElementState();
    this.onChange(this.value);
    this.onTouched();
  }

  @HostListener('b3-blur')
  onB3Blur(): void {
    this.onTouched();
  }

  @HostListener('b3-focus')
  onB3Focus(): void {
    this.applyPopoverLeftOffset();
  }

  @HostListener('click')
  onHostClick(): void {
    window.setTimeout(() => this.applyPopoverLeftOffset(), 0);
  }

  private syncElementState(): void {
    const element = this.elementRef.nativeElement;
    element.value = this.value ? this.dateToIso(this.value) : '';
    element.disabled = this.disabled || this.formDisabled;
    element.error = this.error;
    element.background = this.background;
    element.locale = this.locale;

    this.setOptionalAttribute(element, 'label', this.label);
    this.setOptionalAttribute(element, 'placeholder', this.placeholder);
    this.setOptionalAttribute(element, 'hint', this.hint);
    this.setOptionalAttribute(element, 'background', this.background);
    this.setOptionalAttribute(element, 'locale', this.locale);
    this.setOptionalAttribute(element, 'min', this.min);
    this.setOptionalAttribute(element, 'max', this.max);
    this.setOptionalAttribute(element, 'value', element.value);

    if (this.inputId) {
      element.id = this.inputId;
      this.renderer.setAttribute(element, 'id', this.inputId);
    }
  }

  private dateToIso(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private isoToDate(iso: string): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!match) {
      return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(year, month - 1, day);

    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }

    return date;
  }

  private setOptionalAttribute(element: B3DsDatepickerElement, name: string, value: string): void {
    if (value) {
      (element as unknown as Record<string, string>)[name] = value;
      this.renderer.setAttribute(element, name, value);
      return;
    }

    this.renderer.removeAttribute(element, name);
  }

  private applyPopoverLeftOffset(): void {
    const offset = this.popoverLeftOffset.trim();

    if (!offset) {
      return;
    }

    const host = this.elementRef.nativeElement;

    const apply = (): void => {
      const shadow = host.shadowRoot;

      if (shadow) {
        let styleEl = shadow.getElementById(B3DsDatepickerDirective.POPOVER_OFFSET_STYLE_ID);

        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = B3DsDatepickerDirective.POPOVER_OFFSET_STYLE_ID;
          shadow.appendChild(styleEl);
        }

        styleEl.textContent = `.b3-datepicker__popover { left: ${offset} !important; }`;
      }

      const popover =
        shadow?.querySelector('.b3-datepicker__popover') ??
        (this.inputId ? document.getElementById(`${this.inputId}-popover`) : null);

      (popover as HTMLElement | null)?.style.setProperty('left', offset, 'important');
    };

    apply();
    requestAnimationFrame(() => apply());
    window.setTimeout(() => apply(), 50);
  }
}
